import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { ChevronDown, Plus, Wallet } from "lucide-react";
import useWallet from "../../../../../hooks/useWallet";
import { useProvidersConnectReady } from "@layerswap/wallet-core";
import { useSelectedAccount, useSelectSwapAccount } from "../../../../../context/swapAccounts";
import { useConnectModal } from "../../../../../components/Wallet/WalletModal";
import AddressIcon from "../../../../../components/Common/AddressIcon";
import { Address as AddressClass } from "../../../../../lib/address/Address";
import VaulDrawer from "../../../../../components/Modal/vaulModal";
import { WalletItem } from "../../../../../components/Wallet/WalletComponents/WalletsList";
import { ImageWithFallback } from "@layerswap/ui-kit/components";
import { useAddressName } from "../../../../../stores/addressBookStore";
const DestinationWalletPicker = ({ address, destination, token }) => {
    const [open, setOpen] = useState(false);
    const { setFieldValue } = useFormikContext();
    const { wallets: allWallets, providers } = useWallet();
    const selectDestinationAccount = useSelectSwapAccount("to");
    const { connect } = useConnectModal();
    // Treat manually-added addresses (set in other flows) as no-selection so
    // the picker prompts for a wallet rather than surfacing a Manual entry.
    const rawAccount = useSelectedAccount("to", destination?.name);
    const account = rawAccount?.id === 'manually_added' ? undefined : rawAccount;
    // When a destination is set, only show wallets that can autofill that
    // network. When destination is empty, fall back to every connected wallet
    // that supports autofill anywhere — that way the picker still works before
    // the user has picked a destination.
    const walletsToShow = useMemo(() => {
        if (destination) {
            return allWallets.filter(w => w.autofillSupportedNetworks?.some(n => n === destination.name));
        }
        return allWallets.filter(w => (w.autofillSupportedNetworks?.length ?? 0) > 0);
    }, [allWallets, destination?.name]);
    const provider = useMemo(() => {
        if (!destination)
            return undefined;
        return providers.find(p => p.autofillSupportedNetworks?.includes(destination.name));
    }, [providers, destination?.name]);
    const handleSelect = (props) => {
        setFieldValue('destination_address', props.address, true);
        selectDestinationAccount({
            address: props.address,
            id: props.walletId,
            providerName: props.providerName,
        });
        setOpen(false);
    };
    const handleConnect = async () => {
        const result = await connect(provider);
        if (!result)
            return;
        const supportsDestination = !destination || result.autofillSupportedNetworks?.some(n => n === destination.name);
        if (supportsDestination) {
            handleSelect({
                address: result.address,
                walletId: result.id,
                providerName: result.providerName,
            });
        }
    };
    // When the destination changes to a network with no compatible wallet,
    // skip the picker drawer (which would only show "Connect new wallet") and
    // open the connect screen directly, scoped to that network's provider. We
    // track the destination we last auto-prompted for so dismissing without
    // connecting doesn't keep re-firing. Skipped when no wallet is connected
    // at all — the form's non-dismissable connect modal handles that case.
    // Gated on provider readiness: firing before the destination's provider
    // has hydrated would open the modal unscoped and, because the prompted-for
    // ref is already set, never retry scoped once the provider is ready.
    const hasAnyWallet = allWallets.length > 0;
    const providersReady = useProvidersConnectReady();
    const lastAutoPromptedForRef = useRef(undefined);
    useEffect(() => {
        if (!hasAnyWallet)
            return;
        if (!destination)
            return;
        if (account?.address)
            return;
        if (!providersReady)
            return;
        if (lastAutoPromptedForRef.current === destination.name)
            return;
        lastAutoPromptedForRef.current = destination.name;
        handleConnect();
    }, [hasAnyWallet, destination?.name, account?.address, providersReady]);
    const hasAddress = !!address;
    const walletIconSrc = account?.icon;
    const walletName = account?.displayName?.split('-')[0] || 'Connected wallet';
    const addr = hasAddress && destination ? new AddressClass(address, destination) : undefined;
    const savedName = useAddressName(address, destination);
    const shortAddress = addr?.toShortString();
    return (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", onClick: () => setOpen(true), className: "w-full bg-secondary-500 hover:bg-secondary-400/70 rounded-xl pl-3.5 pr-0 py-3 transition-colors flex items-center outline-hidden", children: [_jsx("div", { className: "inline-flex items-center justify-center rounded-lg h-7 w-7 overflow-hidden shrink-0 bg-secondary-400 text-primary-text", children: hasAddress && walletIconSrc ? (_jsx(ImageWithFallback, { src: walletIconSrc, alt: walletName, width: "28", height: "28", className: "h-7 w-7 object-contain" })) : hasAddress && destination ? (_jsx(AddressIcon, { address: new AddressClass(address, destination).full, size: 28, providerName: destination?.type })) : (_jsx(Wallet, { className: "h-4 w-4 text-secondary-text" })) }), _jsxs("div", { className: "ml-2 flex flex-col grow overflow-hidden min-w-0 text-left", children: [_jsx("p", { className: `text-base leading-5 font-medium truncate ${hasAddress ? 'text-primary-text' : 'text-secondary-text'}`, children: hasAddress ? (savedName ?? shortAddress) : 'Select wallet' }), _jsx("p", { className: "text-secondary-text text-sm font-normal leading-4 truncate whitespace-nowrap", children: hasAddress ? (savedName ? shortAddress : walletName) : 'Pick destination wallet' })] }), _jsx("span", { className: "ml-auto px-2 pointer-events-none text-primary-text shrink-0", children: _jsx(ChevronDown, { className: "h-4 w-4 text-secondary-text", "aria-hidden": "true" }) })] }), _jsx(VaulDrawer, { show: open, setShow: setOpen, header: "Receive in", modalId: "destinationWallet", children: _jsxs(VaulDrawer.Snap, { id: "item-1", className: "pb-4 space-y-3", children: [_jsx("button", { type: "button", onClick: handleConnect, className: "w-full flex justify-center p-2 bg-secondary-500 rounded-md hover:bg-secondary-400", children: _jsxs("div", { className: "flex items-center text-secondary-text gap-1 px-3 py-1", children: [_jsx(Plus, { className: "h-4 w-4" }), _jsx("span", { className: "text-sm", children: "Connect new wallet" })] }) }), walletsToShow.length > 0 && (_jsx("div", { className: "flex flex-col gap-2", children: walletsToShow.map((wallet, index) => (_jsx(WalletItem, { account: wallet, selectable: true, token: token, network: destination, onWalletSelect: handleSelect, selectedAddress: address }, `${index}${wallet.providerName}`))) }))] }) })] }));
};
export default DestinationWalletPicker;
